import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import { Typography } from '@mui/material_modern'
import DataGridWithSearchV2 from 'components/generic/datagrid/DataGridWithSearchV2'
import StyledLink from 'components/generic/typography/StyledLink.jsx'
import { DISPATCH_STATUS_ICONS_DICT } from 'components/containers/load-board/utils/constants'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter.jsx'
import { SORTABLE_ASSET_HISTORY_FIELDS } from 'utils/constants'
import { generateRenderHeaderFunction } from 'utils/generateRenderHeaderFunction.jsx'

const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0),
      fontWeight: 450,
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
  },
}))

const getColumns = (renderHeaderWithSort, defaultYardTimezone) => [
  {
    field: 'ticketNumber',
    headerName: 'Ticket Number',
    align: 'left',
    flex: 1,
    sortable: false,
    renderCell: params => <StyledLink to="">{params.row.ticketNumber}</StyledLink>,
  },
  {
    field: 'lastUpdated',
    headerName: 'Last Updated',
    align: 'left',
    flex: 1,
    sortable: false,
    renderCell: ({ row }) => (row.lastUpdated ? dateFormatter(row.lastUpdated, defaultYardTimezone) : '---'),
    renderHeader: () =>
      renderHeaderWithSort('Last Updated', SORTABLE_ASSET_HISTORY_FIELDS.LAST_UPDATED, { startAscending: false }),
  },
  {
    field: 'statusName',
    headerName: 'Status',
    align: 'left',
    flex: 1,
    sortable: false,
    renderCell: ({ row }) => {
      const { status, statusName } = row || {}
      return (
        <>
          {DISPATCH_STATUS_ICONS_DICT[status]?.previewModal}
          <Typography pl={1} variant="body2">
            {statusName}
          </Typography>
        </>
      )
    },
  },
  {
    field: 'ticketType',
    headerName: 'Ticket Type',
    align: 'left',
    flex: 1,
    sortable: false,
  },
  {
    field: 'daysOnSite',
    headerName: 'Days On Site',
    align: 'left',
    flex: 1,
    sortable: false,
  },
  {
    field: 'fromLocation',
    headerName: 'From Location',
    align: 'left',
    flex: 1,
    sortable: false,
  },
  {
    field: 'toLocation',
    headerName: 'To Location',
    align: 'left',
    flex: 1,
    sortable: false,
  },
]

const AssetHistoryGrid = ({ filter, setFilter, ...restProps }) => {
  const classes = useStyles()
  const navigate = useNavigate()
  const { defaultYardTimezone } = useSelector(state => state.auth)

  const renderHeaderWithSort = useMemo(() => generateRenderHeaderFunction(filter, setFilter), [filter])

  const tableColumns = getColumns(renderHeaderWithSort, defaultYardTimezone)

  const handleRowClick = row => {
    navigate(`/app/load-board/ticket-history/${row.id}`)
  }

  return (
    <DataGridWithSearchV2
      {...restProps}
      filter={filter}
      setFilter={setFilter}
      useBackendSortAndFilter
      className={classes.root}
      columns={tableColumns}
      textSearchPlaceholder="Search"
      onRowClick={handleRowClick}
      autoPageSize={false}
    />
  )
}

AssetHistoryGrid.propTypes = DataGridWithSearchV2.propTypes

AssetHistoryGrid.defaultProps = DataGridWithSearchV2.defaultProps

export default AssetHistoryGrid
